/**
 * Data Room Documents
 *
 * Complete list of data room documents based on the Inverio methodology.
 * Each document is mapped to funding stages and scoring dimensions.
 */

import type { DataRoomDocument, ScoringDimension } from './types';

export const DATA_ROOM_DOCUMENTS: DataRoomDocument[] = [
  // ============================================
  // CORPORATE & LEGAL
  // ============================================
  {
    id: 'corp-1',
    category: 'corporate-legal',
    name: 'Certificate of Incorporation',
    description: 'Original incorporation documents',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-2',
    category: 'corporate-legal',
    name: 'Articles of Association / Bylaws',
    description: 'Company governing documents',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-3',
    category: 'corporate-legal',
    name: 'Shareholder Agreement',
    description: 'Rights and obligations of shareholders',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-4',
    category: 'corporate-legal',
    name: 'Cap Table (Fully Diluted)',
    description: 'All shares, options, SAFEs, convertibles',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-5',
    category: 'corporate-legal',
    name: 'Board Resolutions & Minutes',
    description: 'All board decisions documented',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-6',
    category: 'corporate-legal',
    name: 'Stockholder Consents',
    description: 'Written consents in lieu of meetings',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-7',
    category: 'corporate-legal',
    name: 'Good Standing Certificates',
    description: 'All jurisdictions of operation',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-8',
    category: 'corporate-legal',
    name: 'Organizational Chart (Legal)',
    description: 'Legal entity structure',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-9',
    category: 'corporate-legal',
    name: 'Investor Rights Agreement',
    description: 'Prior round investor rights',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-10',
    category: 'corporate-legal',
    name: 'Right of First Refusal / Co-Sale',
    description: 'ROFR and co-sale provisions',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-11',
    category: 'corporate-legal',
    name: 'Voting Agreements',
    description: 'Shareholder voting arrangements',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-12',
    category: 'corporate-legal',
    name: 'Stock Purchase Agreements',
    description: 'All prior round documentation',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-13',
    category: 'corporate-legal',
    name: 'SAFE / Convertible Notes',
    description: 'Outstanding convertible instruments',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-14',
    category: 'corporate-legal',
    name: '409A Valuation',
    description: 'Last 2-3 independent valuations',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-15',
    category: 'corporate-legal',
    name: 'Stock Option Plan',
    description: 'Equity incentive plan documents',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-16',
    category: 'corporate-legal',
    name: 'Option Grant Ledger',
    description: 'All grants with details',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-17',
    category: 'corporate-legal',
    name: 'Legal Opinion Letters',
    description: 'Counsel opinions on key matters',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'if-any',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'corp-18',
    category: 'corporate-legal',
    name: 'D&O Questionnaires',
    description: 'Director and officer disclosures',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // FINANCIAL
  // ============================================
  {
    id: 'fin-1',
    category: 'financial',
    name: 'Monthly Burn Rate & Runway',
    description: 'Current cash consumption analysis',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-2',
    category: 'financial',
    name: 'Bank Statements',
    description: 'Last 6-12 months all accounts',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-3',
    category: 'financial',
    name: 'P&L Statement',
    description: 'Profit and loss (monthly)',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-4',
    category: 'financial',
    name: 'Balance Sheet',
    description: 'Assets, liabilities, equity',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-5',
    category: 'financial',
    name: 'Cash Flow Statement',
    description: 'Operating, investing, financing',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-6',
    category: 'financial',
    name: 'Financial Projections (3-5 yr)',
    description: 'Revenue, costs, cash flow model',
    supportsDimensions: ['business-model', 'financial-health'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-7',
    category: 'financial',
    name: 'Use of Funds Breakdown',
    description: 'How investment will be deployed',
    supportsDimensions: ['business-model'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-8',
    category: 'financial',
    name: 'Audited Financials',
    description: 'Independent audit report',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'recommended',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-9',
    category: 'financial',
    name: 'Revenue Breakdown',
    description: 'By customer/product/geography',
    supportsDimensions: ['traction', 'business-model'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-10',
    category: 'financial',
    name: 'Unit Economics Analysis',
    description: 'CAC, LTV, payback, margins',
    supportsDimensions: ['business-model'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-11',
    category: 'financial',
    name: 'Accounts Receivable Aging',
    description: 'Outstanding customer payments',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-12',
    category: 'financial',
    name: 'Accounts Payable Aging',
    description: 'Outstanding vendor payments',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-13',
    category: 'financial',
    name: 'Debt Schedule',
    description: 'All loans and obligations',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-14',
    category: 'financial',
    name: 'Related Party Transactions',
    description: 'Transactions with insiders',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-15',
    category: 'financial',
    name: 'Budget vs Actuals',
    description: 'Variance analysis',
    supportsDimensions: ['execution-velocity'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-16',
    category: 'financial',
    name: 'Tax Returns',
    description: 'Last 3 years, all jurisdictions',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-17',
    category: 'financial',
    name: 'Transfer Pricing Documentation',
    description: 'Intercompany pricing policies',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'if-any',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-18',
    category: 'financial',
    name: 'Revenue Recognition Policy',
    description: 'GAAP/IFRS compliance documentation',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'fin-19',
    category: 'financial',
    name: 'SOX Compliance Assessment',
    description: 'Internal controls readiness',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'none',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // COMMERCIAL
  // ============================================
  {
    id: 'com-1',
    category: 'commercial',
    name: 'Pitch Deck (Current)',
    description: 'Investor presentation',
    supportsDimensions: ['vision-story'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-2',
    category: 'commercial',
    name: 'Executive Summary',
    description: 'Company overview document',
    supportsDimensions: ['vision-story'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-3',
    category: 'commercial',
    name: 'Product Roadmap',
    description: 'Development timeline and priorities',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-4',
    category: 'commercial',
    name: 'Competitive Analysis',
    description: 'Market positioning and competitors',
    supportsDimensions: ['competition'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-5',
    category: 'commercial',
    name: 'Customer List',
    description: 'Active customers with contract values',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-6',
    category: 'commercial',
    name: 'Sales Pipeline / Funnel',
    description: 'Opportunities and conversion rates',
    supportsDimensions: ['traction', 'gtm-execution'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-7',
    category: 'commercial',
    name: 'Customer Case Studies',
    description: 'Success stories and testimonials',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-8',
    category: 'commercial',
    name: 'Pricing Strategy',
    description: 'Pricing model and rationale',
    supportsDimensions: ['business-model'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-9',
    category: 'commercial',
    name: 'Go-to-Market Strategy',
    description: 'Sales and marketing approach',
    supportsDimensions: ['gtm-execution'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-10',
    category: 'commercial',
    name: 'Top Customer Contracts',
    description: 'Top 10-20 customer agreements',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-11',
    category: 'commercial',
    name: 'Customer Concentration',
    description: 'Revenue dependency analysis',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-12',
    category: 'commercial',
    name: 'Churn Analysis',
    description: 'Customer retention metrics',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-13',
    category: 'commercial',
    name: 'Cohort Analysis',
    description: 'Revenue/retention by cohort',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-14',
    category: 'commercial',
    name: 'NPS / Satisfaction Scores',
    description: 'Customer feedback metrics',
    supportsDimensions: ['traction'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-15',
    category: 'commercial',
    name: 'Partnership Agreements',
    description: 'Strategic partner contracts',
    supportsDimensions: ['gtm-execution'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-16',
    category: 'commercial',
    name: 'Channel / Reseller Agreements',
    description: 'Distribution partner contracts',
    supportsDimensions: ['gtm-execution'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'com-17',
    category: 'commercial',
    name: 'Sales Team Structure',
    description: 'Org, quotas, territories',
    supportsDimensions: ['gtm-execution'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // IP (INTELLECTUAL PROPERTY)
  // ============================================
  {
    id: 'ip-1',
    category: 'ip',
    name: 'IP Assignment Agreements',
    description: 'Founder and employee assignments',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-2',
    category: 'ip',
    name: 'PIIA Agreements',
    description: 'Proprietary info and invention assignment',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-3',
    category: 'ip',
    name: 'IP Asset Inventory',
    description: 'All patents, TMs, copyrights, domains',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-4',
    category: 'ip',
    name: 'Patent Applications & Grants',
    description: 'Full patent portfolio with status',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-5',
    category: 'ip',
    name: 'Trademark Registrations',
    description: 'All registered and pending marks',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-6',
    category: 'ip',
    name: 'Copyright Registrations',
    description: 'Registered copyrights',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-7',
    category: 'ip',
    name: 'Open Source Audit',
    description: 'OSS usage and license compliance',
    supportsDimensions: ['defensibility', 'product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-8',
    category: 'ip',
    name: 'License Agreements (Inbound)',
    description: 'Third-party IP licenses used',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-9',
    category: 'ip',
    name: 'License Agreements (Outbound)',
    description: 'IP licensed to third parties',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-10',
    category: 'ip',
    name: 'Freedom to Operate Opinion',
    description: 'FTO analysis for key products',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'recommended',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-11',
    category: 'ip',
    name: 'IP Litigation History',
    description: 'Past or pending IP disputes',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'ip-12',
    category: 'ip',
    name: 'Trade Secret Policies',
    description: 'Protection procedures',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // HR & TEAM
  // ============================================
  {
    id: 'hr-1',
    category: 'hr-team',
    name: 'Founder Bios / CVs',
    description: 'Background and experience',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-2',
    category: 'hr-team',
    name: 'Org Chart (Functional)',
    description: 'Reporting structure',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-3',
    category: 'hr-team',
    name: 'Key Employee List',
    description: 'Titles, start dates, compensation',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-4',
    category: 'hr-team',
    name: 'Employment Agreement Template',
    description: 'Standard employment contract',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-5',
    category: 'hr-team',
    name: 'Full Employee Roster',
    description: 'All employees with equity grants',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-6',
    category: 'hr-team',
    name: 'Offer Letter Templates',
    description: 'Standard offer documentation',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-7',
    category: 'hr-team',
    name: 'Employee Handbook',
    description: 'Policies and procedures',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-8',
    category: 'hr-team',
    name: 'Contractor Agreements',
    description: 'Independent contractor contracts',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-9',
    category: 'hr-team',
    name: 'Non-Compete / Non-Solicit',
    description: 'Restrictive covenant agreements',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-10',
    category: 'hr-team',
    name: 'Key Employee Retention',
    description: 'Retention packages / golden handcuffs',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'if-any',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-11',
    category: 'hr-team',
    name: 'Commission / Bonus Plans',
    description: 'Variable compensation structures',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-12',
    category: 'hr-team',
    name: 'Benefits Summary',
    description: 'Health, 401k, perks',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-13',
    category: 'hr-team',
    name: 'HR Policies Manual',
    description: 'Complete HR policy documentation',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-14',
    category: 'hr-team',
    name: 'D&I Data / Policies',
    description: 'Diversity and inclusion reporting',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'recommended',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-15',
    category: 'hr-team',
    name: 'Employee Turnover Analysis',
    description: 'Retention metrics and trends',
    supportsDimensions: ['team', 'execution-velocity'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-16',
    category: 'hr-team',
    name: 'Employment Disputes',
    description: 'Pending or threatened claims',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'hr-17',
    category: 'hr-team',
    name: 'Immigration Status (Key)',
    description: 'Visa status for key employees',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // TECHNOLOGY
  // ============================================
  {
    id: 'tech-1',
    category: 'technology',
    name: 'Product Demo / Sandbox',
    description: 'Access to working product',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-2',
    category: 'technology',
    name: 'Technical Architecture',
    description: 'System design overview',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-3',
    category: 'technology',
    name: 'Technology Stack',
    description: 'Languages, frameworks, tools',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'required',
      seed: 'required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-4',
    category: 'technology',
    name: 'System Architecture Diagrams',
    description: 'Detailed technical diagrams',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-5',
    category: 'technology',
    name: 'Data Flow Documentation',
    description: 'How data moves through system',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-6',
    category: 'technology',
    name: 'API Documentation',
    description: 'External and internal APIs',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-7',
    category: 'technology',
    name: 'Development Methodology',
    description: 'Agile, sprints, release process',
    supportsDimensions: ['execution-velocity'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-8',
    category: 'technology',
    name: 'QA / Testing Processes',
    description: 'Quality assurance procedures',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-9',
    category: 'technology',
    name: 'Uptime / SLA History',
    description: 'Service availability metrics',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-10',
    category: 'technology',
    name: 'Security Certifications',
    description: 'SOC 2, ISO 27001, etc.',
    supportsDimensions: ['product', 'defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'recommended',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-11',
    category: 'technology',
    name: 'SOC 2 Type II Report',
    description: 'Independent security audit',
    supportsDimensions: ['product', 'defensibility'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-12',
    category: 'technology',
    name: 'Penetration Test Results',
    description: 'Security vulnerability testing',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'recommended',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-13',
    category: 'technology',
    name: 'Data Privacy Compliance',
    description: 'GDPR, CCPA documentation',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-14',
    category: 'technology',
    name: 'Incident Response Plan',
    description: 'Security incident procedures',
    supportsDimensions: ['product'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-15',
    category: 'technology',
    name: 'Business Continuity / DR',
    description: 'Disaster recovery plan',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-16',
    category: 'technology',
    name: 'Third-Party Dependencies',
    description: 'External service reliance',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-17',
    category: 'technology',
    name: 'Technical Debt Assessment',
    description: 'Code quality and refactoring needs',
    supportsDimensions: ['product', 'scalability'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'tech-18',
    category: 'technology',
    name: 'Infrastructure Cost Analysis',
    description: 'Cloud/hosting cost breakdown',
    supportsDimensions: ['scalability', 'financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // COMPLIANCE
  // ============================================
  {
    id: 'comp-1',
    category: 'compliance',
    name: 'Regulatory Licenses',
    description: 'Required industry licenses',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'if-required',
      seed: 'if-required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'comp-2',
    category: 'compliance',
    name: 'Compliance Policies',
    description: 'AML, KYC, data protection',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'if-required',
      seed: 'if-required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'comp-3',
    category: 'compliance',
    name: 'Regulatory Correspondence',
    description: 'Communications with regulators',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'comp-4',
    category: 'compliance',
    name: 'Pending Regulatory Matters',
    description: 'Open investigations or issues',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'comp-5',
    category: 'compliance',
    name: 'Environmental Permits',
    description: 'Environmental compliance',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'if-required',
      seed: 'if-required',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // CONTRACTS
  // ============================================
  {
    id: 'cont-1',
    category: 'contracts',
    name: 'Lease Agreements',
    description: 'Office and facility leases',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-2',
    category: 'contracts',
    name: 'Equipment Leases',
    description: 'Leased equipment contracts',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-3',
    category: 'contracts',
    name: 'Insurance Policies',
    description: 'D&O, E&O, cyber, general liability',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'basic',
      seed: 'basic',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-4',
    category: 'contracts',
    name: 'Major Vendor Contracts',
    description: 'Contracts >$50K annually',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-5',
    category: 'contracts',
    name: 'Loan Agreements',
    description: 'All debt instruments',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-6',
    category: 'contracts',
    name: 'Guarantees',
    description: 'Personal or corporate guarantees',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-7',
    category: 'contracts',
    name: 'Material Contract Summary',
    description: 'Schedule of key contracts',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'cont-8',
    category: 'contracts',
    name: 'Change of Control Provisions',
    description: 'CoC triggers in contracts',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // LITIGATION
  // ============================================
  {
    id: 'lit-1',
    category: 'litigation',
    name: 'Pending Litigation',
    description: 'Active lawsuits or claims',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'lit-2',
    category: 'litigation',
    name: 'Threatened Litigation',
    description: 'Demand letters, disputes',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'lit-3',
    category: 'litigation',
    name: 'Settled Litigation',
    description: 'Past 3-5 years settlements',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'if-any',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'lit-4',
    category: 'litigation',
    name: 'Regulatory Investigations',
    description: 'Government inquiries',
    supportsDimensions: ['financial-health', 'market'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'lit-5',
    category: 'litigation',
    name: 'IP Infringement Claims',
    description: 'Patent/TM/copyright disputes',
    supportsDimensions: ['defensibility'],
    requirements: {
      'pre-seed': 'if-any',
      seed: 'if-any',
      'series-a': 'required',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },

  // ============================================
  // ESG & GOVERNANCE
  // ============================================
  {
    id: 'esg-1',
    category: 'esg-governance',
    name: 'Board Composition',
    description: 'Independence and diversity',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-2',
    category: 'esg-governance',
    name: 'Committee Charters',
    description: 'Audit, comp, nom/gov charters',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'none',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-3',
    category: 'esg-governance',
    name: 'Corporate Governance Guidelines',
    description: 'Board governance policies',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'none',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-4',
    category: 'esg-governance',
    name: 'Code of Ethics / Conduct',
    description: 'Employee ethics standards',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-5',
    category: 'esg-governance',
    name: 'Whistleblower Policy',
    description: 'Anonymous reporting procedures',
    supportsDimensions: ['team'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-6',
    category: 'esg-governance',
    name: 'Related Party Policy',
    description: 'Conflict of interest procedures',
    supportsDimensions: ['financial-health'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'required',
      'pre-ipo': 'required',
    },
  },
  {
    id: 'esg-7',
    category: 'esg-governance',
    name: 'ESG Metrics / Reporting',
    description: 'Sustainability data',
    supportsDimensions: ['market'],
    requirements: {
      'pre-seed': 'none',
      seed: 'none',
      'series-a': 'none',
      'series-b-plus': 'none',
      'pre-ipo': 'required',
    },
  },
];

// Get documents by category
export function getDocumentsByCategory(category: string): DataRoomDocument[] {
  return DATA_ROOM_DOCUMENTS.filter((doc) => doc.category === category);
}

// Get documents required for a specific stage
export function getDocumentsForStage(
  stage: 'pre-seed' | 'seed' | 'series-a' | 'series-b-plus' | 'pre-ipo'
): DataRoomDocument[] {
  return DATA_ROOM_DOCUMENTS.filter((doc) => {
    const req = doc.requirements[stage];
    return req === 'required' || req === 'basic';
  });
}

// Get recommended documents for a stage
export function getRecommendedDocumentsForStage(
  stage: 'pre-seed' | 'seed' | 'series-a' | 'series-b-plus' | 'pre-ipo'
): DataRoomDocument[] {
  return DATA_ROOM_DOCUMENTS.filter((doc) => {
    const req = doc.requirements[stage];
    return req === 'recommended' || req === 'if-any' || req === 'if-required';
  });
}

// Get all documents supporting a dimension
export function getDocumentsByDimension(dimension: ScoringDimension): DataRoomDocument[] {
  return DATA_ROOM_DOCUMENTS.filter((doc) => doc.supportsDimensions.includes(dimension));
}
